/**
 * 🔌 DHL USB-Stick Service (ESP32-S3-GEEK HID Maus+Tastatur + 2-Geräte-Queue + Scanner-Auge-Brücke)
 *
 * Steuert den physischen DHL SmartScanner im Paketshop per WLAN → USB-HID:
 *  - Multi-Paket-Ausgabe mit nur 1 Unterschrift (Empfänger oder Bevollmächtigter)
 *  - Schnelleinlagerung mit 3-Minuten-Fast-Path (1. Scan wartet 2,8 s Ladezeit, Folgescans sofort)
 *  - 2-Geräte-Warteschlange (prüft ob Kanal frei ist; reiht ein und priorisiert AUSGABE vor SCHNELLEINLAGERUNG)
 *  - Dauer-Wachhalter (verhindert Standby & blindes PIN-Tippen in offene Masken)
 *  - Kamera-Brücke ("Scanner-Auge"): Relais für Arbeits-Handys + automatisches Kontrollbild nach jedem Job
 *
 * Strikt auf das Konto `wiesn2022@gmail.com` beschränkt.
 */
import { dhlRein } from './dhlBridge';
import { aktueller as aktuellerMitarbeiter } from './mitarbeiter';
import { getAuthUser } from '../utils/packageMatchingHelpers';
import { relaySignal } from './stationService';
import { api } from './apiService';

const STICK_IP_KEY = 'kiosk_dhl_stick_ip';
const STICK_IP_LOCAL_KEY = 'kiosk_dhl_stick_ip_local';
const STICK_ON_KEY = 'kiosk_dhl_stick_on';
const STICK_ROLE_KEY = 'kiosk_dhl_stick_role'; // '1' = Scanner 1 (Schnelleinl. + Ausgabe), '2' = Scanner 2 (Ausgabe)
const BEVOLLM_KEY = 'kiosk_bevollmaechtigter';
const PHOTO_SCHNELL_KEY = 'kiosk_dhl_photo_schnell'; // Foto -> sofort Schnelleinlagerung starten
const STICK_QUEUE_MODE_KEY = 'kiosk_dhl_stick_queue_mode'; // 'auto' | 'review'
const STICK_LOCAL_TASKS_KEY = 'kiosk_dhl_stick_tasks';
const STICK_BRIDGE_MODE_KEY = 'kiosk_dhl_stick_bridge_mode'; // '1' = Dieses Handy ist Kamera-Brücke über Scanner 1
const MAX_BARCODES_PER_STICK_JOB = 10;

// Verifizierte Kamera-Koordinaten (v2) für den DHL SmartScanner
export const VERIFIED_DHL_COORDS_V2 = {
  maxX: 230,
  maxY: 580,
  ausgabeX: 33.0,
  ausgabeY: 35.0,
  schnellX: 33.0,
  schnellY: 50.0,
  empfaengerX: 33.0,
  empfaengerY: 22.0,
  bevollmX: 68.0,
  bevollmY: 20.0,
  plusX: 74.0,
  plusY: 82.0,
  textfeldX: 35.0,
  textfeldY: 23.0,
  weiterX: 95.0,
  weiterY: 82.0,
  homeX: 50.0,
  homeY: 82.0,
  backX: 12.0,
  backY: 82.0,
  nameLastX: 35.0,
  nameLastY: 23.0,
  nameFirstX: 35.0,
  nameFirstY: 33.0,
  schnellConfirmX: 95.0,
  schnellConfirmY: 82.0,
  sigX1: 15.0,
  sigY1: 22.0,
  sigX2: 85.0,
  sigY2: 42.0,
};

// Offene Relay-Requests (Arbeits-Handy wartet auf Antwort der Kamera-Brücke per SSE)
const pendingRelayWaiters = new Map();
let lastAutoCalibratedIp = '';

if (typeof window !== 'undefined') {
  window.addEventListener('kiosk:dhl-stick-relay-res', (ev) => {
    const d = ev?.detail;
    if (!d || !d.reqId) return;
    const waiter = pendingRelayWaiters.get(d.reqId);
    if (waiter) {
      pendingRelayWaiters.delete(d.reqId);
      clearTimeout(waiter.timer);
      waiter.resolve(d.result || { ok: false, error: 'Leere Antwort von Kamera-Brücke' });
    }
  });
}

/**
 * Strikt nur für das Konto `wiesn2022@gmail.com` freigeschaltet!
 * Alle anderen Konten sehen die DHL-Stick-, Bevollmächtigten-, Warteschlangen- und Briefmarken-Module nicht.
 */
export function isWiesnAccount() {
  try {
    let email = '';
    const token = localStorage.getItem('kiosk_jwt_access');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.email) email = String(payload.email).trim().toLowerCase();
      } catch { /* ignore */ }
    }
    if (!email) {
      const u = getAuthUser?.();
      email = String(u?.email || localStorage.getItem('kiosk_user_email') || '').trim().toLowerCase();
    }
    return email === 'wiesn2022@gmail.com';
  } catch {
    return false;
  }
}

export function isBevollmaechtigterEnabled() {
  if (!isWiesnAccount()) return false;
  try {
    return localStorage.getItem(BEVOLLM_KEY) === '1';
  } catch {
    return false;
  }
}

export function setBevollmaechtigterEnabled(on) {
  try {
    localStorage.setItem(BEVOLLM_KEY, on ? '1' : '0');
    window.dispatchEvent(new CustomEvent('kiosk:custom-features'));
  } catch { /* ignore */ }
}

export function isPhotoSchnelleinlagerungEnabled() {
  if (!isDhlStickEnabled()) return false;
  try {
    const val = localStorage.getItem(PHOTO_SCHNELL_KEY);
    return val !== '0';
  } catch {
    return true;
  }
}

export function setPhotoSchnelleinlagerungEnabled(on) {
  try {
    localStorage.setItem(PHOTO_SCHNELL_KEY, on ? '1' : '0');
    window.dispatchEvent(new CustomEvent('kiosk:dhl-stick-queue'));
  } catch { /* ignore */ }
}

export function isScannerEyeBridgeActive() {
  try {
    return localStorage.getItem(STICK_BRIDGE_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setScannerEyeBridgeActive(on) {
  try {
    localStorage.setItem(STICK_BRIDGE_MODE_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
}

export function getDhlStickQueueMode() {
  try {
    return localStorage.getItem(STICK_QUEUE_MODE_KEY) === 'review' ? 'review' : 'auto';
  } catch {
    return 'auto';
  }
}

export function setDhlStickQueueMode(mode) {
  try {
    localStorage.setItem(STICK_QUEUE_MODE_KEY, mode === 'review' ? 'review' : 'auto');
    window.dispatchEvent(new CustomEvent('kiosk:dhl-stick-queue'));
  } catch { /* ignore */ }
}

export function getLocalStickTasks() {
  try {
    const arr = JSON.parse(localStorage.getItem(STICK_LOCAL_TASKS_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveLocalStickTasks(tasks) {
  try {
    // Strokes aus abgeschlossenen Tasks entfernen, um localStorage-Quota & Datenschutz zu schonen
    const trimmed = (Array.isArray(tasks) ? tasks : []).slice(0, 25).map((t) => {
      if (t.status === 'sent' && t.payload?.strokes?.length) {
        return { ...t, payload: { ...t.payload, strokes: [] } };
      }
      return t;
    });
    localStorage.setItem(STICK_LOCAL_TASKS_KEY, JSON.stringify(trimmed));
    window.dispatchEvent(new CustomEvent('kiosk:dhl-stick-queue', { detail: trimmed }));
  } catch { /* ignore */ }
}

export function upsertLocalStickTask(task) {
  if (!task || !task.id) return;
  const list = getLocalStickTasks();
  const idx = list.findIndex((t) => t.id === task.id);
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...task, updatedAt: new Date().toISOString() };
  } else {
    list.unshift({
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...task,
    });
  }
  saveLocalStickTasks(list);
}

export function removeLocalStickTask(id) {
  const list = getLocalStickTasks().filter((t) => t.id !== id);
  saveLocalStickTasks(list);
}

export function clearLocalStickTasks() {
  saveLocalStickTasks([]);
}

export function getDhlStickIp() {
  try {
    const saved = (
      localStorage.getItem(STICK_IP_LOCAL_KEY) ||
      localStorage.getItem(STICK_IP_KEY) ||
      ''
    ).trim();
    if (!saved || saved === '192.168.137.51' || saved === '192.168.4.1') {
      localStorage.setItem(STICK_IP_LOCAL_KEY, '192.168.178.57');
      localStorage.setItem(STICK_IP_KEY, '192.168.178.57');
      return '192.168.178.57';
    }
    return saved;
  } catch {
    return '192.168.178.57';
  }
}

export function setDhlStickIp(ip, { localOnly = false } = {}) {
  const clean = String(ip || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  try {
    localStorage.setItem(STICK_IP_LOCAL_KEY, clean);
    if (!localOnly) {
      localStorage.setItem(STICK_IP_KEY, clean);
    }
  } catch { /* ignore */ }
  return clean;
}

export function isDhlStickEnabled() {
  if (!isWiesnAccount()) return false;
  try {
    return localStorage.getItem(STICK_ON_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDhlStickEnabled(on) {
  try {
    localStorage.setItem(STICK_ON_KEY, on ? '1' : '0');
    window.dispatchEvent(new CustomEvent('kiosk:custom-features'));
    window.dispatchEvent(new CustomEvent('kiosk:dhl-stick-queue'));
  } catch { /* ignore */ }
}

export function getDhlStickRole() {
  try {
    return localStorage.getItem(STICK_ROLE_KEY) === '2' ? 2 : 1;
  } catch {
    return 1;
  }
}

export function setDhlStickRole(role) {
  try {
    localStorage.setItem(STICK_ROLE_KEY, Number(role) === 2 ? '2' : '1');
  } catch { /* ignore */ }
}

export function getDhlSourceDeviceLabel() {
  try {
    const ma = aktuellerMitarbeiter?.();
    if (ma && ma.name) return sanitizeDhlHidText(ma.name).slice(0, 14);
    const role = getDhlStickRole();
    return `HANDY G${role}`;
  } catch {
    return 'HANDY';
  }
}

/**
 * Bereinigt Namen & Texte für die deutsche USB-HID-Tastatur des Sticks:
 *  - ä -> ae, ö -> oe, ü -> ue, ß -> ss
 *  - Internationale Akzente (é, ć, š, č, ł, ø, ğ, ş, ñ) -> ASCII-Basisbuchstaben
 *  - Apostrophe (' ` ´) -> Leerzeichen (da ' auf DE-QWERTZ sonst 'ä' tippt!)
 */
export function sanitizeDhlHidText(input = '') {
  const raw = String(input || '').trim();
  if (!raw) return '';
  return raw
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/ẞ/g, 'SS')
    .replace(/[łŁ]/g, (m) => (m === 'Ł' ? 'L' : 'l'))
    .replace(/[øØ]/g, (m) => (m === 'Ø' ? 'O' : 'o'))
    .replace(/[đĐ]/g, (m) => (m === 'Đ' ? 'D' : 'd'))
    .replace(/[ğĞ]/g, (m) => (m === 'Ğ' ? 'G' : 'g'))
    .replace(/[şŞ]/g, (m) => (m === 'Ş' ? 'S' : 's'))
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .replace(/æ/g, 'ae')
    .replace(/Æ/g, 'Ae')
    .replace(/œ/g, 'oe')
    .replace(/Œ/g, 'Oe')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['`´"“”„/\\]/g, ' ')
    .replace(/[^A-Za-z0-9 .\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fordert das Kamera-Station-Handy ("Scanner-Auge") auf, nach `delayMs` ein frisches
 * Kontrollbild vom DHL-Scanner-Display aufzunehmen und per SSE an alle Geräte zu senden.
 */
export function requestDhlScreenSnapshot({ delayMs = 950, reason = '' } = {}) {
  if (!isWiesnAccount()) return;
  try {
    relaySignal('station:dhl-snap-req', { delayMs, reason }).catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Holt das letzte auf dem Server hinterlegte DHL-Scanner-Displaybild.
 */
export async function fetchLatestDhlScreenSnapshot() {
  try {
    const res = await api.get('/station/dhl-screen-latest');
    return res?.snap || null;
  } catch {
    return null;
  }
}

async function stickFetchViaBridgeRelay(path, { method = 'GET', body, ip, timeoutMs = 4800 } = {}) {
  const reqId = `sreq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pendingRelayWaiters.delete(reqId);
      resolve({ ok: false, offline: true, error: 'DHL-Stick (und Kamera-Brücke) nicht erreichbar' });
    }, timeoutMs);
    pendingRelayWaiters.set(reqId, { resolve, timer });
    relaySignal('station:dhl-stick-req', {
      reqId,
      path,
      method,
      body: body || undefined,
      ip: (ip || getDhlStickIp()).trim(),
    }).catch(() => {
      clearTimeout(timer);
      pendingRelayWaiters.delete(reqId);
      resolve({ ok: false, offline: true, error: 'DHL-Stick nicht erreichbar' });
    });
  });
}

export async function stickFetch(path, { method = 'GET', body, ip, timeoutMs = 5500, noBridgeFallback = false } = {}) {
  const targetIp = (ip || getDhlStickIp()).trim();
  if (!targetIp) throw new Error('Keine DHL-Stick-IP konfiguriert');
  const url = `http://${targetIp}${path}`;

  // 📱 Auf Android/iOS (Capacitor) über native CapacitorHttp-Bridge senden,
  // damit WebView Mixed-Content (https://app.gepaeckannahme.de -> http://192.168.x.x) umgangen wird!
  const capHttp = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
    ? window.Capacitor?.Plugins?.CapacitorHttp
    : null;
  if (capHttp && typeof capHttp.request === 'function') {
    try {
      const resp = await capHttp.request({
        url,
        method,
        headers: body ? { 'Content-Type': 'application/json' } : {},
        data: body || undefined,
        connectTimeout: timeoutMs,
        readTimeout: timeoutMs,
      });
      const rawData = resp?.data;
      const data = typeof rawData === 'string'
        ? (() => { try { return JSON.parse(rawData); } catch { return {}; } })()
        : (rawData && typeof rawData === 'object' ? rawData : {});
      const status = Number(resp?.status) || 0;
      const ok = status >= 200 && status < 300 && data.ok !== false;
      if (ok || status > 0) {
        return { ok, status, ...data };
      }
    } catch (err) {
      // Falls dieses Gerät NICHT selbst die Kamera-Brücke ist (z.B. Handy im 5G-Netz hinten im Lager):
      // Automatisch über die Kamera-Brücke an der Theke versuchen!
      if (!noBridgeFallback && !isScannerEyeBridgeActive()) {
        const relayRes = await stickFetchViaBridgeRelay(path, { method, body, ip: targetIp, timeoutMs: 3800 });
        if (relayRes?.ok) return { ...relayRes, viaBridge: true };
      }
      return { ok: false, offline: true, error: err?.message || 'DHL-Stick nicht erreichbar' };
    }
  }

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    // text/plain bei POST verhindert unnötige CORS-Preflight-Blockaden im Browser
    const res = await fetch(url, {
      method,
      headers: body ? { 'Content-Type': 'text/plain' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data.ok !== false, status: res.status, ...data };
  } catch (err) {
    if (!noBridgeFallback && !isScannerEyeBridgeActive()) {
      const relayRes = await stickFetchViaBridgeRelay(path, { method, body, ip: targetIp, timeoutMs: 3800 });
      if (relayRes?.ok) return { ...relayRes, viaBridge: true };
    }
    return {
      ok: false,
      offline: true,
      error: err?.name === 'AbortError' ? 'Zeitüberschreitung zum DHL-Stick' : (err?.message || 'DHL-Stick nicht erreichbar'),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Prüft Live-Status & ob der Kanal auf dem DHL-Stick frei ist.
 * Synchronisiert beim ersten Kontakt automatisch die verifizierten v2-Koordinaten
 * (Bevollmächtigter=68/20, Unterschrift=Y22..42, smartStandby=false) in den Stick!
 */
export async function getDhlStickStatus(ip) {
  let res = await stickFetch('/api/dhl/status', { ip, timeoutMs: 2800 });
  if (!res.ok && !res.offline) {
    res = await stickFetch('/api/status', { ip, timeoutMs: 2800 });
  }
  if (res?.ok) {
    const c = res.coords || {};
    const needsV2Sync =
      res.smartStandby === true ||
      (typeof c.bevollmX === 'number' && c.bevollmX < 50) ||
      (typeof c.sigY2 === 'number' && c.sigY2 > 50);
    const targetIp = (ip || getDhlStickIp()).trim();
    if (needsV2Sync && lastAutoCalibratedIp !== targetIp) {
      lastAutoCalibratedIp = targetIp;
      saveDhlStickConfig({
        ...VERIFIED_DHL_COORDS_V2,
        keepAlive: true,
        smartStandby: false,
      }, targetIp).catch(() => {});
    }
  }
  return res;
}

/**
 * Zerlegt einen vollen Namen ("Max Mustermann", "Anna von der Heide" oder "Mustermann, Max")
 * sauber in { firstName, lastName }.
 */
export function splitPersonName(fullName = '') {
  const raw = String(fullName || '').replace(/\(\d+\)/g, '').trim();
  if (!raw) return { firstName: '', lastName: '' };
  if (raw.includes(',')) {
    const [last, ...rest] = raw.split(',');
    return {
      lastName: sanitizeDhlHidText(last || ''),
      firstName: sanitizeDhlHidText(rest.join(' ')),
    };
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: '', lastName: sanitizeDhlHidText(parts[0]) };
  }
  // Namenszusätze ("von", "van", "de", "der", "del", "zu", "da", "di") zum Nachnamen ziehen
  const particles = new Set(['von', 'van', 'de', 'der', 'den', 'del', 'della', 'zu', 'zum', 'zur', 'da', 'di', 'du', 'la', 'le', 'al', 'el', 'bin', 'ben', 'st', 'st.']);
  let lastStartIdx = parts.length - 1;
  while (lastStartIdx > 1 && particles.has(parts[lastStartIdx - 1].toLowerCase())) {
    lastStartIdx--;
  }
  return {
    firstName: sanitizeDhlHidText(parts.slice(0, lastStartIdx).join(' ')),
    lastName: sanitizeDhlHidText(parts.slice(lastStartIdx).join(' ')),
  };
}

/**
 * Reiht einen Schnelleinlagerungs-Job (1 oder mehrere Barcodes) in die Stick-Warteschlange ein.
 */
export async function enqueueDhlStickSchnelleinlagerung({ barcodes = [], label = '', jobId, ip, bypassReview = false } = {}) {
  const cleanCodes = (Array.isArray(barcodes) ? barcodes : [barcodes])
    .map((c) => dhlRein(c))
    .filter((c) => c.length >= 6);
  if (!cleanCodes.length) {
    return { ok: false, error: 'Kein gültiger DHL-Barcode' };
  }
  const baseId = jobId || `se-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  if (!bypassReview && getDhlStickQueueMode() === 'review') {
    upsertLocalStickTask({
      id: baseId,
      type: 'SCHNELLEINLAGERUNG',
      label: label || `Schnelleinlagerung (${cleanCodes.length})`,
      barcodes: cleanCodes,
      status: 'pending_review',
      payload: { barcodes: cleanCodes, label, jobId: baseId, ip },
    });
    return { ok: true, pendingReview: true, queuedForReview: true, jobId: baseId, barcodeCount: cleanCodes.length };
  }

  upsertLocalStickTask({
    id: baseId,
    type: 'SCHNELLEINLAGERUNG',
    label: label || `Schnelleinlagerung (${cleanCodes.length})`,
    barcodes: cleanCodes,
    status: 'sending',
    payload: { barcodes: cleanCodes, label, jobId: baseId, ip },
  });

  let lastRes = null;
  for (let i = 0; i < cleanCodes.length; i += MAX_BARCODES_PER_STICK_JOB) {
    const chunk = cleanCodes.slice(i, i + MAX_BARCODES_PER_STICK_JOB);
    const id = i === 0 ? baseId : `${baseId}-${i / MAX_BARCODES_PER_STICK_JOB}`;
    lastRes = await stickFetch('/api/dhl/queue', {
      method: 'POST',
      ip,
      body: {
        jobId: id,
        type: 'schnelleinlagerung',
        sourceDevice: getDhlSourceDeviceLabel(),
        barcodes: chunk,
      },
    });
    if (!lastRes?.ok) {
      upsertLocalStickTask({
        id: baseId,
        status: 'error',
        error: lastRes?.error || 'Stick nicht erreichbar',
      });
      return lastRes;
    }
  }
  upsertLocalStickTask({
    id: baseId,
    status: 'sent',
    error: '',
  });
  // Kontrollbild vom Scanner-1-Display nach Einlagerung anfordern (Fast-Path ~1.6s, Erststart ~4.2s)
  requestDhlScreenSnapshot({ delayMs: 1800, reason: `Schnelleinlagerung ${cleanCodes[0].slice(-6)}` });
  requestDhlScreenSnapshot({ delayMs: 4200, reason: `Schnelleinlagerung ${cleanCodes[0].slice(-6)}` });
  return lastRes;
}

/**
 * Reiht eine Ausgabe (1 oder beliebig viele Sendungen mit 1 Unterschrift) in die Stick-Warteschlange ein.
 */
export async function enqueueDhlStickAusgabe({
  barcodes = [],
  mode = 'empfaenger', // 'empfaenger' | 'bevollmaechtigter'
  firstName = '',
  lastName = '',
  fullName = '',
  strokes = [],
  submitFinal = false,
  jobId,
  ip,
  bypassReview = false,
} = {}) {
  const cleanCodes = (Array.isArray(barcodes) ? barcodes : [barcodes])
    .map((c) => dhlRein(c))
    .filter((c) => c.length >= 6);
  if (!cleanCodes.length) {
    return { ok: false, error: 'Keine gültige Sendungsnummer für DHL-Stick' };
  }

  let fName = sanitizeDhlHidText(firstName);
  let lName = sanitizeDhlHidText(lastName);
  // WICHTIG: Niemals bei 'bevollmaechtigter' auf den Empfängernamen (fullName) zurückfallen!
  if (!fName && !lName && fullName && mode !== 'bevollmaechtigter') {
    const split = splitPersonName(fullName);
    fName = split.firstName;
    lName = split.lastName;
  }

  const baseId = jobId || `ausg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const taskLabel = `${mode === 'bevollmaechtigter' ? '👥 Bevollm.' : '📤 Ausgabe'} ${lName || fName || ''} (${cleanCodes.length})`.trim();
  const taskPayload = {
    barcodes: cleanCodes,
    mode,
    firstName: fName,
    lastName: lName,
    fullName: mode === 'bevollmaechtigter' ? '' : fullName,
    strokes: Array.isArray(strokes) ? strokes : [],
    submitFinal: Boolean(submitFinal),
    jobId: baseId,
    ip,
  };

  if (!bypassReview && getDhlStickQueueMode() === 'review') {
    upsertLocalStickTask({
      id: baseId,
      type: 'AUSGABE',
      mode,
      label: taskLabel,
      barcodes: cleanCodes,
      status: 'pending_review',
      payload: taskPayload,
    });
    return { ok: true, pendingReview: true, queuedForReview: true, jobId: baseId, totalBarcodes: cleanCodes.length };
  }

  upsertLocalStickTask({
    id: baseId,
    type: 'AUSGABE',
    mode,
    label: taskLabel,
    barcodes: cleanCodes,
    status: 'sending',
    payload: taskPayload,
  });

  let firstRes = null;
  const totalChunks = Math.ceil(cleanCodes.length / MAX_BARCODES_PER_STICK_JOB);
  for (let i = 0; i < cleanCodes.length; i += MAX_BARCODES_PER_STICK_JOB) {
    const chunk = cleanCodes.slice(i, i + MAX_BARCODES_PER_STICK_JOB);
    const chunkIdx = i / MAX_BARCODES_PER_STICK_JOB;
    const id = chunkIdx === 0 ? baseId : `${baseId}-${chunkIdx}`;
    const res = await stickFetch('/api/dhl/queue', {
      method: 'POST',
      ip,
      body: {
        jobId: id,
        type: 'ausgabe',
        mode: mode === 'bevollmaechtigter' ? 'bevollmaechtigter' : 'empfaenger',
        sourceDevice: getDhlSourceDeviceLabel(),
        firstName: fName,
        lastName: lName,
        barcodes: chunk,
        strokes: Array.isArray(strokes) ? strokes : [],
        submitFinal: Boolean(submitFinal),
      },
    });
    if (!firstRes) firstRes = res;
    if (!res?.ok) {
      upsertLocalStickTask({
        id: baseId,
        status: 'error',
        error: res?.error || 'Stick nicht erreichbar',
      });
      return res;
    }
  }
  upsertLocalStickTask({
    id: baseId,
    status: 'sent',
    error: '',
  });
  requestDhlScreenSnapshot({ delayMs: 4500, reason: `Ausgabe ${lName}` });
  return { ...firstRes, totalBarcodes: cleanCodes.length, totalChunks };
}

/**
 * Führt eine wartende oder fehlgeschlagene Aufgabe aus der lokalen Stick-Warteschlange aus.
 * Behält bei "pending_review" dieselbe jobId; erzeugt nur bei explizitem Retry eine neue ID.
 */
export async function executeLocalStickTask(taskId, { forceNewId = false } = {}) {
  const task = getLocalStickTasks().find((t) => t.id === taskId);
  if (!task || !task.payload) return { ok: false, error: 'Aufgabe nicht gefunden' };
  const useId = forceNewId
    ? `${task.type === 'SCHNELLEINLAGERUNG' ? 'se' : 'ausg'}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`
    : task.id;
  if (task.type === 'SCHNELLEINLAGERUNG') {
    const res = await enqueueDhlStickSchnelleinlagerung({
      ...task.payload,
      jobId: useId,
      bypassReview: true,
    });
    if (taskId !== useId) removeLocalStickTask(taskId);
    return res;
  }
  const res = await enqueueDhlStickAusgabe({
    ...task.payload,
    jobId: useId,
    bypassReview: true,
  });
  if (taskId !== useId) removeLocalStickTask(taskId);
  return res;
}

/**
 * Schließt eine noch offene Schnelleinlagerungs-Maske am DHL-Scanner mit "Weiter" + Bestätigung ab.
 */
export async function finishDhlStickSchnelleinlagerung(ip) {
  const res = await stickFetch('/api/dhl/schnelleinlagerung/finish', {
    method: 'POST',
    ip,
    body: {},
    timeoutMs: 6500,
  });
  requestDhlScreenSnapshot({ delayMs: 1800, reason: 'Schnelleinlagerung abgeschlossen' });
  return res;
}

/**
 * 🏠 Deterministischer Not-Reset auf den DHL-Startbildschirm:
 * Klickt START (50/80) und anschließend JA (95/76), falls "Vorgang abbrechen?" offen ist,
 * und leert die Stick-Queue. Funktioniert sowohl mit neuer als auch alter Stick-Firmware!
 */
export async function safeHomeDhlStick(ip) {
  const res = await stickFetch('/api/dhl/safe-home', {
    method: 'POST',
    ip,
    body: {},
    timeoutMs: 5500,
  });
  if (!res.ok && !res.offline) {
    // Fallback für ältere Firmware über /api/dhl/queue/clear + /api/macro
    await clearDhlStickQueue(ip);
    const macroRes = await stickFetch('/api/macro', {
      method: 'POST',
      ip,
      body: {
        steps: [
          { op: 'tap', x: 50, y: 80 },
          { op: 'wait', ms: 460 },
          { op: 'tap', x: 95, y: 76 },
        ],
      },
    });
    requestDhlScreenSnapshot({ delayMs: 1400, reason: 'Not-Reset auf Start' });
    return macroRes;
  }
  requestDhlScreenSnapshot({ delayMs: 1400, reason: 'Not-Reset auf Start' });
  return res;
}

/**
 * 🗑️ Entfernt die oberste/zuletzt gescannte Sendungsnummer aus der offenen DHL-Liste (35/22 -> 32/80).
 */
export async function removeLastDhlStickItem(ip) {
  const res = await stickFetch('/api/dhl/remove-last', {
    method: 'POST',
    ip,
    body: {},
    timeoutMs: 5500,
  });
  if (!res.ok && !res.offline) {
    const macroRes = await stickFetch('/api/macro', {
      method: 'POST',
      ip,
      body: {
        steps: [
          { op: 'tap', x: 35, y: 22 },
          { op: 'wait', ms: 360 },
          { op: 'tap', x: 32, y: 80 },
        ],
      },
    });
    requestDhlScreenSnapshot({ delayMs: 1200, reason: 'Nummer entfernt' });
    return macroRes;
  }
  requestDhlScreenSnapshot({ delayMs: 1200, reason: 'Nummer entfernt' });
  return res;
}

/**
 * ⌨️ Schaltet auf dem DHL-Scanner einmalig die Android-Bildschirmtastatur frei,
 * obwohl der USB-Stick als physische Tastatur eingesteckt ist.
 */
export async function enableVirtualKeyboardDhlStick(ip) {
  const res = await stickFetch('/api/dhl/enable-vkbd', {
    method: 'POST',
    ip,
    body: {},
    timeoutMs: 7500,
  });
  if (!res.ok && !res.offline) {
    return stickFetch('/api/macro', {
      method: 'POST',
      ip,
      body: {
        steps: [
          { op: 'tap', x: 35, y: 18 },
          { op: 'wait', ms: 500 },
          { op: 'swipe', x1: 50, y1: 2, x2: 50, y2: 75, durationMs: 240 },
          { op: 'wait', ms: 600 },
          { op: 'tap', x: 50, y: 32 },
          { op: 'wait', ms: 600 },
          { op: 'tap', x: 72, y: 42 },
          { op: 'wait', ms: 450 },
          { op: 'tap', x: 50, y: 80 },
        ],
      },
      timeoutMs: 7500,
    });
  }
  return res;
}

/**
 * 📡 Drahtloses OTA-Firmware-Update des DHL-Sticks direkt aus der App:
 * Lädt `/dhl-stick-firmware.bin` vom Kiosk-Server und flasht den Stick über WLAN (`POST /api/ota`).
 */
export async function otaUpdateDhlStick(ip) {
  const targetIp = (ip || getDhlStickIp()).trim();
  if (!targetIp) return { ok: false, error: 'Keine Stick-IP' };
  try {
    const binRes = await fetch(`/dhl-stick-firmware.bin?t=${Date.now()}`);
    if (!binRes.ok) {
      return { ok: false, error: `Firmware-Datei nicht gefunden (HTTP ${binRes.status})` };
    }
    const blob = await binRes.blob();
    const form = new FormData();
    form.append('update', blob, 'firmware.bin');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 25000);
    try {
      const upRes = await fetch(`http://${targetIp}/api/ota`, {
        method: 'POST',
        body: form,
        signal: ctrl.signal,
      });
      const data = await upRes.json().catch(() => ({}));
      return { ok: upRes.ok && data.ok !== false, ...data };
    } finally {
      clearTimeout(timer);
    }
  } catch (err) {
    return { ok: false, error: err?.message || 'OTA-Upload fehlgeschlagen' };
  }
}

/**
 * Speichert Kalibrierungs-/Auflösungs-/Standby-Einstellungen dauerhaft im Stick (NVS).
 */
export async function saveDhlStickConfig(cfg = {}, ip) {
  return stickFetch('/api/dhl/config', {
    method: 'POST',
    ip,
    body: cfg,
  });
}

/**
 * Tippt auf eine Prozent-Koordinate (0..100) am DHL-Scanner (für Finger-Kalibrierung & Live-Bild-Fernsteuerung).
 */
export async function tapDhlStickCoord({ xPct, yPct, maxX, maxY, click = true, ip } = {}) {
  const res = await stickFetch('/api/calibrate', {
    method: 'POST',
    ip,
    body: { xPct, yPct, maxX, maxY, click },
  });
  requestDhlScreenSnapshot({ delayMs: 900, reason: `Tap ${Math.round(xPct)}%/${Math.round(yPct)}%` });
  return res;
}

/**
 * Weckt den DHL-Scanner sofort auf und gibt die PIN ein.
 */
export async function wakeupDhlStick(ip) {
  const res = await stickFetch('/api/dhl/wakeup', {
    method: 'POST',
    ip,
    body: { force: true },
    timeoutMs: 8000,
  });
  requestDhlScreenSnapshot({ delayMs: 2800, reason: 'Wakeup + PIN' });
  return res;
}

/**
 * Leert die Warteschlange auf dem DHL-Stick.
 */
export async function clearDhlStickQueue(ip) {
  return stickFetch('/api/dhl/queue/clear', {
    method: 'POST',
    ip,
    body: {},
  });
}

/**
 * 🧠 Lokale 15ms-Bildanalyse des zugeschnittenen DHL-Scanner-Displays (ohne KI-Latenz/Kosten!):
 * Erkennt anhand der charakteristischen DHL-Farbzonen:
 *  - Standby / Display aus (dunkel)
 *  - Sperrbildschirm / PIN-Eingabe (kein gelber DHL-Header)
 *  - Unterschrifts-Maske (großes weißes Feld bei Y=20..44%)
 *  - Popup / Fehlermeldung / Dialog offen (helles/rotes Dialogfeld in der Bildschirmmitte)
 *  - Scan-Liste / Schnelleinlagerung (gelber SCANNEN-Balken bei Y=72..77% + ob graue Paketzeile bei Y=20..26% existiert)
 *  - Startbildschirm / Menü
 */
export function analyzeDhlScreenCanvas(canvas) {
  try {
    const w = canvas.width;
    const h = canvas.height;
    if (!w || !h) return { state: 'unknown', label: 'Kein Bild', color: '#64748b' };
    const ctx = canvas.getContext('2d');
    const img = ctx.getImageData(0, 0, w, h).data;

    const sampleRegion = (x0Pct, y0Pct, x1Pct, y1Pct) => {
      const x0 = Math.max(0, Math.floor((x0Pct / 100) * w));
      const x1 = Math.min(w - 1, Math.ceil((x1Pct / 100) * w));
      const y0 = Math.max(0, Math.floor((y0Pct / 100) * h));
      const y1 = Math.min(h - 1, Math.ceil((y1Pct / 100) * h));
      let r = 0, g = 0, b = 0, count = 0, yellowLike = 0, blueLike = 0, whiteLike = 0, grayRowLike = 0;
      const stepX = Math.max(1, Math.floor((x1 - x0) / 18));
      const stepY = Math.max(1, Math.floor((y1 - y0) / 14));
      for (let y = y0; y <= y1; y += stepY) {
        for (let x = x0; x <= x1; x += stepX) {
          const idx = (y * w + x) * 4;
          const pr = img[idx], pg = img[idx + 1], pb = img[idx + 2];
          r += pr; g += pg; b += pb; count++;
          // DHL-Gelb (auch unter Kamera-Weißabgleich: Rot & Grün deutlich höher als Blau)
          if (pr > 125 && pg > 115 && (pr + pg) / 2 - pb > 22) yellowLike++;
          // DHL-Hellblau (Blau dominiert über Rot)
          if (pb > 130 && pg > 110 && pb - pr > 12) blueLike++;
          // Sehr hell / Weiß (Unterschriftsfeld oder weißes Dialog-Popup)
          if (pr > 205 && pg > 205 && pb > 200 && Math.abs(pr - pb) < 25) whiteLike++;
          // Graublaue Listen-Zeile (erfasste Sendungsnummer bei Y=20..26%)
          if (pb > 110 && pr > 80 && pb - pr < 35 && pg < 175) grayRowLike++;
        }
      }
      if (!count) return { luma: 0, yellowRatio: 0, blueRatio: 0, whiteRatio: 0, grayRowRatio: 0 };
      r /= count; g /= count; b /= count;
      return {
        r, g, b,
        luma: 0.299 * r + 0.587 * g + 0.114 * b,
        yellowRatio: yellowLike / count,
        blueRatio: blueLike / count,
        whiteRatio: whiteLike / count,
        grayRowRatio: grayRowLike / count,
      };
    };

    const full = sampleRegion(10, 10, 90, 90);
    if (full.luma < 42) {
      return {
        state: 'standby',
        label: '⚫ Display aus / Standby',
        color: '#ef4444',
        isAsleep: true,
        hasListItem: false,
        popupSuspected: false,
      };
    }

    const header = sampleRegion(20, 5, 80, 14);
    const mainMid = sampleRegion(18, 28, 82, 58);
    const sigBox = sampleRegion(18, 22, 82, 43);
    const scanBar = sampleRegion(25, 72, 75, 78);
    const footer = sampleRegion(10, 80, 90, 90);
    const listRow1 = sampleRegion(15, 20, 85, 26);
    const listRowEmptyRef = sampleRegion(15, 32, 85, 38);

    const hasDhlHeader = header.yellowRatio > 0.22 || footer.yellowRatio > 0.22;
    if (!hasDhlHeader && full.blueRatio < 0.18) {
      return {
        state: 'lockscreen',
        label: '🔐 Sperrbildschirm / Android',
        color: '#f59e0b',
        isAsleep: true,
        hasListItem: false,
        popupSuspected: false,
      };
    }

    // Großes weißes Unterschriftsfeld oben + kein gelber SCANNEN-Balken bei 75%
    if (sigBox.whiteRatio > 0.55 && scanBar.yellowRatio < 0.25) {
      return {
        state: 'signature',
        label: '✍️ Unterschrifts-Maske offen',
        color: '#a855f7',
        isAsleep: false,
        hasListItem: false,
        popupSuspected: false,
      };
    }

    // Weißes/helles Popup-Fenster mitten auf dem blauen Bildschirm (z.B. 18+, Fehler, Tastatur-Dialog)
    if (mainMid.whiteRatio > 0.48 && sigBox.whiteRatio < 0.45) {
      return {
        state: 'popup',
        label: '⚠️ Hinweis-/Fehler-Popup am Scanner!',
        color: '#ef4444',
        isAsleep: false,
        hasListItem: false,
        popupSuspected: true,
      };
    }

    // Gelber SCANNEN-Balken bei 75% sichtbar -> Scan-Liste (Schnelleinlagerung / Ausgabe-Scan / Annahme)
    if (scanBar.yellowRatio > 0.25) {
      // Prüfe ob Zeile 1 (Y=20..26%) dunkler/grauer ist als der leere blaue Bereich darunter (Y=32..38%)
      const lumaDiff = listRowEmptyRef.luma - listRow1.luma;
      const hasListItem = lumaDiff > 8 || listRow1.blueRatio < listRowEmptyRef.blueRatio - 0.15;
      return {
        state: 'scan_list',
        label: hasListItem ? '🟢 Scan-Liste (Sendung erfasst ✅)' : '🔵 Scan-Maske offen (Liste leer)',
        color: hasListItem ? '#10b981' : '#38bdf8',
        isAsleep: false,
        hasListItem,
        popupSuspected: false,
      };
    }

    return {
      state: 'menu_or_input',
      label: '🏠 DHL-Menü / Eingabemaske aktiv',
      color: '#10b981',
      isAsleep: false,
      hasListItem: false,
      popupSuspected: false,
    };
  } catch {
    return { state: 'unknown', label: 'Bild empfangen', color: '#38bdf8' };
  }
}

/**
 * Komprimiert ein Foto der Vollmacht im Browser auf ein kompaktes JPEG (< 160 KB Data-URL).
 */
export function compressVollmachtPhoto(fileOrDataUrl, { maxDim = 960, quality = 0.58 } = {}) {
  return new Promise((resolve, reject) => {
    const loadImg = (src) => {
      const img = new Image();
      img.onload = () => {
        try {
          let curDim = maxDim;
          let curQuality = quality;
          let dataUrl = '';
          for (let attempt = 0; attempt < 4; attempt++) {
            let w = img.width || 800;
            let h = img.height || 600;
            if (w > curDim || h > curDim) {
              if (w >= h) {
                h = Math.round((h * curDim) / w);
                w = curDim;
              } else {
                w = Math.round((w * curDim) / h);
                h = curDim;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            dataUrl = canvas.toDataURL('image/jpeg', curQuality);
            if (dataUrl.length <= 165000) break;
            curDim = Math.round(curDim * 0.78);
            curQuality = Math.max(0.35, curQuality - 0.12);
          }
          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error('Vollmacht-Foto konnte nicht geladen werden'));
      img.src = src;
    };

    if (typeof fileOrDataUrl === 'string') {
      loadImg(fileOrDataUrl);
    } else if (fileOrDataUrl instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (ev) => loadImg(ev.target.result);
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(fileOrDataUrl);
    } else {
      reject(new Error('Ungültiges Bildformat'));
    }
  });
}
